import { z } from 'zod'
import { localeSchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import type { MessageErrorCode, MessageStatus } from './messaging.js'
import type { OrgUnitRef } from './org-units.js'
import { isPermission, type ModuleKey, type Permission } from './permissions.js'

/** NIST 800-63B style: length over composition rules. */
export const passwordSchema = z
  .string()
  .min(10, 'Use at least 10 characters')
  .max(256)
  .refine((v) => v.trim().length === v.length, 'Password cannot start or end with a space')

const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(254))

export const userStatusSchema = z.enum(['ACTIVE', 'DISABLED'])
export type UserStatus = z.infer<typeof userStatusSchema>

/**
 * The data scope of one role assignment (ADR-025, 01 §4): which records the role's permissions reach.
 * `ALL`: every record (the default, and what every assignment was before scopes); `ORG_SUBTREE`: records
 * whose owning unit is in the subtree of a listed unit; `ASSIGNED`: records the user is a member of;
 * `OWN`: records the user created or owns.
 */
export const SCOPE_KINDS = ['ALL', 'ORG_SUBTREE', 'ASSIGNED', 'OWN'] as const
export const scopeKindSchema = z.enum(SCOPE_KINDS)
export type ScopeKind = z.infer<typeof scopeKindSchema>

/** At most this many units on one `ORG_SUBTREE` assignment. */
export const MAX_SCOPE_UNITS = 50

export const roleScopeSchema = z
  .object({
    roleId: z.uuid(),
    kind: scopeKindSchema,
    orgUnitIds: z
      .array(z.uuid())
      .max(MAX_SCOPE_UNITS)
      .default([])
      .transform((ids) => [...new Set(ids)]),
  })
  .superRefine((v, ctx) => {
    if (v.kind === 'ORG_SUBTREE' && v.orgUnitIds.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['orgUnitIds'], message: 'Choose at least one unit' })
    } else if (v.kind !== 'ORG_SUBTREE' && v.orgUnitIds.length > 0) {
      ctx.addIssue({ code: 'custom', path: ['orgUnitIds'], message: 'Only an org-subtree scope lists units' })
    }
  })
export type RoleScopeInput = z.input<typeof roleScopeSchema>
export type RoleScope = z.output<typeof roleScopeSchema>

/**
 * `scopes` on a user payload is optional: a role it omits keeps its current scope on update, and a role
 * new to the user gets `ALL`. A scope for a role not in `roleIds`, or two scopes for one role, are refused.
 */
const scopesField = z.array(roleScopeSchema).max(20).optional()

function scopesMatchRoles(v: { roleIds: string[]; scopes?: RoleScope[] | undefined }, ctx: z.RefinementCtx) {
  const roles = new Set(v.roleIds)
  const seen = new Set<string>()
  v.scopes?.forEach((scope, i) => {
    if (!roles.has(scope.roleId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['scopes', i, 'roleId'],
        message: 'The user does not hold this role',
      })
    } else if (seen.has(scope.roleId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['scopes', i, 'roleId'],
        message: 'This role already has a scope',
      })
    }
    seen.add(scope.roleId)
  })
}

export const userCreateSchema = z
  .object({
    email: emailSchema,
    name: z.string().trim().min(1).max(200),
    nameAr: optionalText(200),
    phone: optionalText(30),
    locale: localeSchema.default('en'),
    roleIds: z.array(z.uuid()).max(20).default([]),
    scopes: scopesField,
    /**
     * E-mail the user an invitation to set their own password (R0b MS9) instead of giving them one.
     * The password is then left out; without an invitation it is required.
     */
    invite: z.boolean().default(false),
    /** Initial password; the user must change it at first sign-in. */
    password: passwordSchema.optional(),
  })
  .superRefine(scopesMatchRoles)
  .superRefine((v, ctx) => {
    if (v.invite && v.password !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['password'],
        message: 'Leave the password empty: the invitation lets the user set it',
      })
    } else if (!v.invite && v.password === undefined) {
      ctx.addIssue({ code: 'custom', path: ['password'], message: 'Enter a password or send an invitation' })
    }
  })
export type UserCreate = z.input<typeof userCreateSchema>

/** POST /users/:id/invite (and a create with `invite`): the invitation e-mail that was queued. */
export interface UserInvitationIssued {
  /** When the link stops working (72 hours after it was sent). */
  expiresAt: string
  /** The address it went to, masked (`a***@example.com`). */
  recipientMasked: string
}

/**
 * Where a user's latest invitation stands (GET /users/:id/invitation):
 * - PENDING: the e-mail is queued or being sent;
 * - SENT: the e-mail went out and the link still works;
 * - FAILED: the e-mail could not be sent (`messageErrorCode` says why); the link never arrived and
 *   is revoked once delivery gives up;
 * - EXPIRED: the link ran out before it was used;
 * - ACCEPTED: the user set their password;
 * - REVOKED: an administrator withdrew it (or it was replaced by a newer one).
 */
export const INVITATION_STATES = ['PENDING', 'SENT', 'FAILED', 'EXPIRED', 'ACCEPTED', 'REVOKED'] as const
export type InvitationState = (typeof INVITATION_STATES)[number]

export interface UserInvitationStatus {
  id: string
  state: InvitationState
  /** The address it went to, masked; null once erased. */
  recipientMasked: string | null
  createdAt: string
  expiresAt: string
  usedAt: string | null
  revokedAt: string | null
  /** The e-mail's own delivery state (null when it cannot be found any more). */
  messageStatus: MessageStatus | null
  messageErrorCode: MessageErrorCode | null
}

/** GET /users/:id/invitation: the latest invitation, or null when the user was never invited. */
export interface UserInvitationView {
  invitation: UserInvitationStatus | null
}

/**
 * The fields of a user update without the cross-field scope check, for forms that pick or extend them
 * (zod refuses `.omit()` on a refined object). Requests are validated with `userUpdateSchema`.
 */
export const userUpdateFieldsSchema = z.object({
  email: emailSchema,
  name: z.string().trim().min(1).max(200),
  nameAr: optionalText(200),
  phone: optionalText(30),
  locale: localeSchema,
  status: userStatusSchema,
  roleIds: z.array(z.uuid()).max(20),
  scopes: scopesField,
  version: versionSchema,
})
export const userUpdateSchema = userUpdateFieldsSchema.superRefine(scopesMatchRoles)
export type UserUpdate = z.input<typeof userUpdateSchema>

export const passwordResetSchema = z.object({ password: passwordSchema })
export type PasswordReset = z.infer<typeof passwordResetSchema>

/**
 * POST /auth/invitations/:token (public): the invited user sets a password and the invitation is used
 * up. Any unknown, used, revoked or expired token answers INVITATION_INVALID.
 */
export const acceptInvitationSchema = z.object({ password: passwordSchema }).strict()
export type AcceptInvitation = z.infer<typeof acceptInvitationSchema>

/** GET /auth/invitations/:token: what the set-password page shows (never the inviter's details). */
export interface InvitationInfo {
  email: string
  name: string
  companyName: string
  locale: 'en' | 'ar'
  expiresAt: string
}

export const passwordChangeSchema = z
  .object({ currentPassword: z.string().min(1).max(256), newPassword: passwordSchema })
  .refine((v) => v.currentPassword !== v.newPassword, {
    path: ['newPassword'],
    message: 'Choose a password different from the current one',
  })
export type PasswordChange = z.infer<typeof passwordChangeSchema>

/**
 * `PUT /users/:id/home-unit` (needs `orgUnits.manage`): the user's home org unit (ADR-025, 01 §3), or
 * null for none. A newly chosen unit must be active.
 */
export const userHomeUnitSchema = z.object({
  orgUnitId: z.uuid().nullable(),
  version: versionSchema,
})
export type UserHomeUnit = z.input<typeof userHomeUnitSchema>

export const userListQuerySchema = pageQuerySchema.extend({
  status: userStatusSchema.optional(),
  roleId: z.uuid().optional(),
  /** Users whose home unit is this unit (not its subunits). */
  orgUnitId: z.uuid().optional(),
})
export type UserListQuery = z.input<typeof userListQuerySchema>

/**
 * A slim people lookup (active users) for pickers outside user administration, e.g. a unit's head on
 * the org chart: open to users.read or orgUnits.manage, so it returns only what a picker shows.
 */
export const userLookupQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
})
export type UserLookupQuery = z.input<typeof userLookupQuerySchema>

export interface UserLookupItem {
  id: string
  name: string
  nameAr: string | null
  email: string
}

export interface RoleRef {
  id: string
  name: string
  nameAr: string | null
}

export interface UserListItem {
  id: string
  email: string
  name: string
  nameAr: string | null
  status: UserStatus
  locked: boolean
  roles: RoleRef[]
  lastLoginAt: string | null
  /**
   * The user's home org unit (archived units included), or null. Always sent by the API; optional in
   * the type only until the web's user fixtures carry it.
   */
  homeOrgUnit?: (OrgUnitRef & { archived: boolean }) | null
}

/** An org unit named by a scope, labelled for display. */
export interface ScopeUnitRef {
  id: string
  code: string
  name: string
  nameAr: string | null
  archived: boolean
}

/** The scope of one of the user's role assignments, as stored. */
export interface RoleScopeView {
  roleId: string
  kind: ScopeKind
  /** Empty unless `kind` is `ORG_SUBTREE`. */
  orgUnits: ScopeUnitRef[]
}

export interface User extends UserListItem {
  phone: string | null
  locale: 'en' | 'ar'
  mustChangePassword: boolean
  /** The scope of each role assignment, one entry per role in `roles`, in the same order. */
  roleScopes: RoleScopeView[]
  version: number
  createdAt: string
  updatedAt: string
}

/**
 * The union of the scopes under which a user holds one permission: any `ALL` wins, subtree units merge,
 * and `OWN`/`ASSIGNED` are flags.
 */
export interface EffectiveScope {
  all: boolean
  orgUnits: ScopeUnitRef[]
  own: boolean
  assigned: boolean
}

/** `GET /users/:id/access`: what a user may reach, per role assignment and per permission. */
export interface UserAccessView {
  userId: string
  /** A role grants every permission (Administrator); such a grant is always `ALL`. */
  holdsEverything: boolean
  grants: {
    role: RoleRef
    kind: ScopeKind
    orgUnits: ScopeUnitRef[]
    /**
     * Every permission of the role belongs to a module that is off: the scope is kept and takes effect
     * when the module is back (ADR-024).
     */
    moduleOff: boolean
    /**
     * Some permission of the role is narrowed by a data scope (`isScopeEnforced`). False: whatever
     * `kind` says, the role still reaches every record (its modules do not honour scopes yet).
     */
    enforced: boolean
  }[]
  permissions: {
    permission: Permission
    /** `null` for administration permissions, which no module switch removes. */
    module: ModuleKey | null
    /** Granted, but its module is off, so it is not effective now. */
    moduleOff: boolean
    scope: EffectiveScope
    /**
     * Its readers honour data scopes (`isScopeEnforced`). False: it reaches every record whatever
     * `scope` says; the scope is kept and applies once its module honours scopes.
     */
    enforced: boolean
    /** The roles that grant it. */
    roleIds: string[]
  }[]
}

/**
 * A non-blocking note on a user save. `SCOPE_NOT_ENFORCED`: a scope other than ALL was put on roles
 * none of whose permissions honour scopes yet, so those roles still reach every record.
 */
export interface UserSaveWarning {
  code: 'SCOPE_NOT_ENFORCED'
  roleIds: string[]
}

/** `POST /users` and `PUT /users/:id`: the saved user and any warnings about the save. */
export interface UserSaved extends User {
  /** Always sent by the API; optional in the type only until the web's fixtures carry it (absent: none). */
  warnings?: UserSaveWarning[]
}

const permissionKey = z.string().refine(isPermission, 'Unknown permission')

export const roleInputSchema = z.object({
  name: z.string().trim().min(1).max(100),
  nameAr: optionalText(100),
  description: optionalText(500),
  descriptionAr: optionalText(500),
  permissions: z
    .array(permissionKey)
    .max(500)
    .default([])
    .transform((v) => [...new Set(v)].sort() as Permission[]),
})
export type RoleInput = z.input<typeof roleInputSchema>

export const roleUpdateSchema = roleInputSchema.extend({ version: versionSchema })
export type RoleUpdate = z.input<typeof roleUpdateSchema>

export interface Role extends RoleRef {
  description: string | null
  descriptionAr: string | null
  isSystem: boolean
  allPermissions: boolean
  permissions: Permission[]
  userCount: number
  version: number
}
